"use client";

import { usePathname } from "next/navigation";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useLockBodyScroll } from "~/lib/hooks/useLockBodyScroll";
import { useSessionStore } from "~/lib/stateManagement/sessionState/sessionStore";

export const useModal = (
	id: string,
	callback: () => void,
	lockedOnDesktop: boolean | undefined = true,
	neverlocked = false,
	openOnLoad = false,
) => {
	const setModal = useSessionStore((state) => state.setModal);
	const clearModal = useSessionStore((state) => state.clearModal);
	const modalId = useSessionStore((state) => state.modalId);

	const { unlockBodyScroll, lockBodyScroll } = useLockBodyScroll(lockedOnDesktop, neverlocked);

	const pathname = usePathname();
	const [prevPath, setPrevPath] = useState<string | null>(pathname ?? null);

	useEffect(() => {
		const handleRouteChange = () => {
			clearModal();
			unlockBodyScroll();
			setPrevPath(pathname);
		};
		if (prevPath !== pathname) {
			handleRouteChange();
		}
	}, [pathname, clearModal, prevPath, unlockBodyScroll]);

	const isOpen = modalId === id;
	const [initialLoad, setInitialLoad] = useState<boolean>(true);

	const openModal = useCallback(() => {
		setModal(id, callback);
		lockBodyScroll();
	}, [id, callback, setModal, lockBodyScroll]);

	useLayoutEffect(() => {
		if (openOnLoad && !isOpen && initialLoad) {
			openModal();
			setInitialLoad(false);
		}
	}, [openModal, openOnLoad, isOpen, initialLoad]);

	const closeModal = isOpen
		? () => {
				clearModal();
				unlockBodyScroll();
			}
		: () => {
				console.error("Modal is not open");
			};

	const toggleModal = isOpen ? closeModal : openModal;

	return {
		isOpen,
		openModal,
		closeModal,
		toggleModal,
		currentModal: modalId,
	};
};

export type IModal = ReturnType<typeof useModal>;
